/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  DefaultValues,
  FieldValues,
  useForm,
  useWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ZodType, ZodTypeDef } from 'zod';
import { FieldConfig } from './field-types';
import { FormField } from './form-field';
import { SubmitButton } from './submit-button';
import {
  IMPORT_JSON_MESSAGES,
  ImportExtra,
  ImportJsonPanel,
  useJsonImport,
} from './import-json';

interface AutoFormProps<TValues extends FieldValues> {
  fields: readonly FieldConfig<TValues>[];
  // Only pin the schema's OUTPUT to TValues. Leaving the INPUT param open
  // (`unknown`) lets schemas whose input differs from output — e.g. any variant
  // using `.default()`, which makes that field optional on input but required on
  // output — match without TS inferring two conflicting TValues (the TS2719
  // "two different types with this name" build failure).
  schema: ZodType<TValues, ZodTypeDef, unknown>;
  defaults: DefaultValues<TValues>;
  isLoading: boolean;
  onSubmit: (values: TValues) => void;
  submitLabel?: string;
  expectedProduct?: string;
  importExtras?: readonly ImportExtra[];
  onImport?: (raw: Record<string, unknown>) => void;
}

export function AutoForm<TValues extends FieldValues>({
  fields,
  schema,
  defaults,
  isLoading,
  onSubmit,
  submitLabel,
  expectedProduct,
  importExtras,
  onImport,
}: AutoFormProps<TValues>) {
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const form = useForm<TValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
    mode: 'onBlur',
  });

  const {
    register, unregister, handleSubmit, reset, getValues, control, formState: { errors },
  } = form;

  const watched = useWatch({ control }) as TValues;
  const isVisible = (field: FieldConfig<TValues>) => (
    field.visibleWhen ? field.visibleWhen(watched) : true
  );

  const hiddenFields = fields.filter((field) => !isVisible(field));
  // Re-run only when the SET of hidden fields changes, not on every keystroke.
  const hiddenSignal = hiddenFields.map((field) => String(field.key)).join('|');

  useEffect(() => {
    hiddenFields.forEach((field) => unregister(field.key, { keepDefaultValue: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenSignal]);

  const jsonImport = useJsonImport({
    formFieldKeys: fields.map((f) => String(f.key)),
    expectedProduct,
    extras: importExtras,
    onApplyFormFields: (picked) => {
      reset({ ...getValues(), ...picked } as TValues);
    },
    onApplyRaw: onImport,
  });

  const handleApplyImport = () => {
    if (jsonImport.apply()) setShowImport(false);
  };

  const toggleImport = () => {
    setShowImport((prev) => {
      const next = !prev;
      if (!next) jsonImport.reset();
      return next;
    });
  };

  // Fields are pre-ordered per section in each fields.ts, so consecutive
  // same-section runs can just be bucketed together without re-sorting.
  const sectionGroups = fields.filter(isVisible).reduce<
  { title?: string; fields: FieldConfig<TValues>[] }[]
  >((groups, field) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.title === field.section) {
      lastGroup.fields.push(field);
    } else {
      groups.push({ title: field.section, fields: [field] });
    }
    return groups;
  }, []);

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={toggleImport}
        >
          {IMPORT_JSON_MESSAGES.toggleButton}
        </button>
        <label className="form-check form-switch form-check-label small mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={showApiKeys}
            onChange={(e) => setShowApiKeys(e.target.checked)}
          />
          Ver keys de API
        </label>
      </div>

      {showImport && (
        <ImportJsonPanel
          value={jsonImport.importText}
          onChange={jsonImport.setImportText}
          onApply={handleApplyImport}
          error={jsonImport.importError}
        />
      )}

      {sectionGroups.map((group, groupIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`${groupIndex}-${group.title ?? 'group'}`} className="row">
          {group.title && (
            <p className={`text-muted mb-2 col-12 ${groupIndex === 0 ? '' : 'mt-4'}`}>
              {group.title}
            </p>
          )}
          {group.fields.map((field) => {
            const errorMessage = errors[field.key] ? String(errors[field.key]?.message) : undefined;
            const inputId = `field-${String(field.key)}`;
            const invalidClass = errorMessage ? 'is-invalid' : '';
            const colClassName = field.colClassName ?? 'col-md-6';

            return (
              <div key={String(field.key)} className={colClassName}>
                <FormField
                  label={field.label}
                  htmlFor={inputId}
                  apiKey={String(field.key)}
                  showApiKey={showApiKeys}
                  error={errorMessage}
                  hint={field.hint}
                >
                  {field.kind === 'date' && (
                    <input
                      id={inputId}
                      type="date"
                      className={`form-control ${invalidClass}`}
                      {...register(field.key)}
                    />
                  )}

                  {field.kind === 'number' && (
                    <input
                      id={inputId}
                      type="number"
                      step={field.step ?? 'any'}
                      className={`form-control ${invalidClass}`}
                      onWheel={(e) => e.currentTarget.blur()}
                      {...register(field.key)}
                    />
                  )}

                  {field.kind === 'select' && (
                    <select
                      id={inputId}
                      className={`form-select ${invalidClass}`}
                      {...register(field.key)}
                    >
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}

                  {field.kind === 'custom' && field.render(form)}
                </FormField>
              </div>
            );
          })}
        </div>
      ))}

      <div className="mt-3">
        <SubmitButton isLoading={isLoading} label={submitLabel} />
      </div>
    </form>
  );
}
